import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';
import RefreshIcon from '@material-ui/icons/Refresh';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { BoltHoursDialog } from './BoltHoursDialog';

/**
 * Top-of-tab control bar for the Bolt 2-way integration:
 * - Current ONLINE / OFFLINE / UNKNOWN badge
 * - "Last updated …" timestamp
 * - Go Online / Go Offline toggle (with optional reason on offline)
 * - "Sync Hours to Bolt" button (pushes our bolt_opening_hours table)
 * - "Edit Bolt Hours" opens BoltHoursDialog
 *
 * Provider status is mirrored locally in external_order_sources, so this
 * bar always reflects the last known state we sent or received from
 * Bolt's provider-status webhook.
 */

const useStyles = makeStyles((theme) => ({
  bar: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  statusBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Separator that pushes the right slot (refresh + counter + toggle)
  // all the way to the end of the bar when there's room. Flex-wrap on
  // the parent means it falls below cleanly on narrow screens.
  spacer: {
    flex: 1,
    minWidth: 16,
  },
  chipOnline: {
    background: '#1b5e20',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipOffline: {
    background: '#b71c1c',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipUnknown: {
    background: '#757575',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

type Status = 'online' | 'offline' | null;

const formatRelative = (iso: string | null): string => {
  if (!iso) {
    return 'never synced';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString();
};

interface Props {
  shopId: number;
  // Optional extra content rendered on the right end of the bar — used
  // by BoltOrdersPreviewView to drop the orders Refresh button, the
  // active/completed counter, and the "Show completed" toggle into the
  // same header instead of a separate bar below.
  rightSlot?: React.ReactNode;
}

export const BoltProviderStatusBar: React.FC<Props> = ({
  shopId,
  rightSlot,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [status, setStatus] = React.useState<Status>(null);
  const [updatedAt, setUpdatedAt] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [pending, setPending] = React.useState<string | null>(null);
  const [offlineReasonOpen, setOfflineReasonOpen] = React.useState(false);
  const [offlineReason, setOfflineReason] = React.useState('');
  const [hoursOpen, setHoursOpen] = React.useState(false);

  const refresh = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.getBoltProviderState(shopId)
      .then((r) => {
        setStatus(r.providerStatus);
        setUpdatedAt(r.providerStatusUpdatedAt);
      })
      .catch(() => {
        setStatus(null);
        setUpdatedAt(null);
      })
      .finally(() => setLoading(false));
  }, [shopId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleGoOnline = async () => {
    setPending('online');
    try {
      await ExternalOrderService.boltGoOnline(shopId);
      snackbar.open('Bolt: now online, accepting orders');
      refresh();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setPending(null);
    }
  };

  const handleConfirmOffline = async () => {
    setOfflineReasonOpen(false);
    setPending('offline');
    try {
      await ExternalOrderService.boltGoOffline(
        shopId,
        offlineReason.trim() || undefined,
      );
      snackbar.open('Bolt: now offline, no new orders');
      setOfflineReason('');
      refresh();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setPending(null);
    }
  };

  const handleSyncHours = async () => {
    setPending('hours');
    try {
      const result = await ExternalOrderService.boltSyncHours(shopId);
      const n = (result as any)?.data?.hours_sent ?? 0;
      snackbar.open(`Sent ${n} day${n === 1 ? '' : 's'} of opening hours to Bolt`);
    } catch (e) {
      snackbar.error(e);
    } finally {
      setPending(null);
    }
  };

  const statusChip = (() => {
    if (status === 'online') {
      return <Chip label="ONLINE" className={classes.chipOnline} />;
    }
    if (status === 'offline') {
      return <Chip label="OFFLINE" className={classes.chipOffline} />;
    }
    return <Chip label="UNKNOWN" className={classes.chipUnknown} />;
  })();

  return (
    <React.Fragment>
      <Paper className={classes.bar} elevation={1}>
        <div className={classes.statusBlock}>
          <Typography variant="subtitle2" style={{ marginRight: 4 }}>
            Bolt provider:
          </Typography>
          {statusChip}
          <Tooltip title="Refresh status from server">
            <IconButton size="small" onClick={refresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <span className={classes.meta}>
            {loading ? 'loading…' : `last update: ${formatRelative(updatedAt)}`}
          </span>
        </div>

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={pending !== null || status === 'online'}
            onClick={handleGoOnline}
            startIcon={
              pending === 'online' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Go Online
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={pending !== null || status === 'offline'}
            onClick={() => setOfflineReasonOpen(true)}
            startIcon={
              pending === 'offline' ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Go Offline
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={pending !== null}
            onClick={handleSyncHours}
            startIcon={
              pending === 'hours' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ScheduleIcon fontSize="small" />
              )
            }
          >
            Sync Hours
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setHoursOpen(true)}
            disabled={pending !== null}
          >
            Edit Hours
          </Button>
        </div>

        {rightSlot && (
          <React.Fragment>
            <div className={classes.spacer} />
            {rightSlot}
          </React.Fragment>
        )}
      </Paper>

      <Dialog
        open={offlineReasonOpen}
        onClose={() => setOfflineReasonOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Go offline?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bolt will stop sending new orders to your provider until you go
            online again. Orders already received continue to flow through.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason (optional)"
            fullWidth
            value={offlineReason}
            onChange={(e) => setOfflineReason(e.target.value)}
            placeholder="e.g. kitchen overwhelmed, end of shift"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfflineReasonOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmOffline}
            variant="contained"
            color="secondary"
          >
            Go Offline
          </Button>
        </DialogActions>
      </Dialog>

      <BoltHoursDialog
        open={hoursOpen}
        onClose={() => setHoursOpen(false)}
      />
    </React.Fragment>
  );
};
